import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/AdminAddLessonModal.css';

const AdminAddLessonModal = ({ isOpen, onClose, onSuccess }) => {
  const [tutors, setTutors] = useState([]);
  const [tutees, setTutees] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState('');
  const [selectedTutee, setSelectedTutee] = useState('');
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [formData, setFormData] = useState({
    subject: '',
    date: '',
    duration: 60,
    summary: '',
    rate: '',
    tutorRate: '',
    mode: 'Online'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchTutorsAndTutees();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedTutee && selectedTutor) {
      fetchAvailableSubjects();
    } else {
      setAvailableSubjects([]);
      setFormData(prev => ({ ...prev, subject: '', rate: '', tutorRate: '' }));
    }
  }, [selectedTutee, selectedTutor]);

  // Filter tutees based on selected tutor — only show tutees that have this tutor assigned
  const filteredTutees = selectedTutor
    ? tutees.filter(tutee => 
        tutee.subjects?.some(subject => 
          subject.isActive && 
          subject.assignedTutors?.some(at => {
            if (!at) return false;
            const tutorObj = at.tutor;
            if (!tutorObj) return false;
            const tutorId = typeof tutorObj === 'object' ? tutorObj._id : tutorObj;
            return String(tutorId) === String(selectedTutor);
          })
        )
      )
    : tutees;

  const handleTutorChange = (e) => {
    const newTutor = e.target.value;
    setSelectedTutor(newTutor);
    // Reset tutee and subject when tutor changes
    setSelectedTutee('');
    setAvailableSubjects([]);
    setFormData(prev => ({ ...prev, subject: '', rate: '', tutorRate: '' }));
  };

  const fetchTutorsAndTutees = async () => {
    try {
      const [tutorsRes, tuteesRes] = await Promise.all([
        adminApi.getTutors(),
        adminApi.getTutees()
      ]);
      setTutors(tutorsRes.data.filter(t => t.isActive !== false));
      setTutees(tuteesRes.data.filter(t => t.isActive !== false));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load tutors and tutees');
    }
  };

  const fetchAvailableSubjects = async () => {
    try {
      const tuteeData = tutees.find(t => t._id === selectedTutee);
      if (!tuteeData || !tuteeData.subjects) {
        setAvailableSubjects([]);
        return;
      }

      // Get subjects where the selected tutor is assigned
      const tutorSubjects = tuteeData.subjects.filter(subject => 
        subject.isActive && 
        subject.assignedTutors?.some(at => {
          if (!at || !at.tutor) return false;
          const tutorId = typeof at.tutor === 'object' ? at.tutor._id : at.tutor;
          return String(tutorId) === String(selectedTutor);
        })
      );

      setAvailableSubjects(tutorSubjects);
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setAvailableSubjects([]);
    }
  };

  const handleSubjectChange = (e) => {
    const subjectName = e.target.value;
    setFormData(prev => ({ ...prev, subject: subjectName }));

    // Auto-populate rates and mode from the subject enrollment
    const subject = availableSubjects.find(s => s.name === subjectName);
    if (subject) {
      const tutorAssignment = subject.assignedTutors?.find(at => {
        if (!at || !at.tutor) return false;
        const tutorId = typeof at.tutor === 'object' ? at.tutor._id : at.tutor;
        return String(tutorId) === String(selectedTutor);
      });

      if (tutorAssignment && typeof tutorAssignment === 'object') {
        setFormData(prev => ({
          ...prev,
          rate: subject.rate || '',
          tutorRate: tutorAssignment.tutorRate || '',
          mode: subject.mode || prev.mode,
          duration: subject.sessionDuration ? subject.sessionDuration * 60 : prev.duration
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          rate: subject.rate || '',
          mode: subject.mode || prev.mode,
          duration: subject.sessionDuration ? subject.sessionDuration * 60 : prev.duration
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarnings([]);

    try {
      const lessonData = {
        tutor: selectedTutor,
        tutee: selectedTutee,
        subject: formData.subject,
        date: new Date(formData.date).toISOString(),
        duration: parseInt(formData.duration),
        summary: formData.summary,
        rate: parseFloat(formData.rate),
        tutorRate: formData.tutorRate ? parseFloat(formData.tutorRate) : undefined,
        mode: formData.mode
      };

      const response = await adminApi.adminCreateLesson(lessonData);
      
      if (response.data.warnings && response.data.warnings.length > 0) {
        setWarnings(response.data.warnings);
        // Show warnings but still consider it a success
        setTimeout(() => {
          onSuccess('Lesson created successfully with warnings');
          handleClose();
        }, 3000);
      } else {
        onSuccess('Lesson created successfully');
        handleClose();
      }
    } catch (err) {
      console.error('Error creating lesson:', err);
      setError(err.response?.data?.message || 'Failed to create lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTutor('');
    setSelectedTutee('');
    setAvailableSubjects([]);
    setFormData({
      subject: '',
      date: '',
      duration: 60,
      summary: '',
      rate: '',
      tutorRate: '',
      mode: 'Online'
    });
    setError('');
    setWarnings([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content admin-add-lesson-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Lesson (Admin)</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {warnings.length > 0 && (
          <div className="alert alert-warning">
            <strong>⚠️ Warnings:</strong>
            <ul>
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
            <p><em>Lesson was created despite these warnings.</em></p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="tutor">Tutor *</label>
              <select
                id="tutor"
                value={selectedTutor}
                onChange={handleTutorChange}
                required
              >
                <option value="">Select Tutor</option>
                {tutors.map(tutor => (
                  <option key={tutor._id} value={tutor._id}>
                    {tutor.code} - {tutor.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tutee">Tutee *</label>
              <select
                id="tutee"
                value={selectedTutee}
                onChange={(e) => setSelectedTutee(e.target.value)}
                required
                disabled={!selectedTutor}
              >
                <option value="">
                  {!selectedTutor ? 'Select tutor first' : 'Select Tutee'}
                </option>
                {filteredTutees.map(tutee => (
                  <option key={tutee._id} value={tutee._id}>
                    {tutee.code} - {tutee.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <select
                id="subject"
                value={formData.subject}
                onChange={handleSubjectChange}
                required
                disabled={!selectedTutor || !selectedTutee || availableSubjects.length === 0}
              >
                <option value="">
                  {!selectedTutor || !selectedTutee 
                    ? 'Select tutor and tutee first' 
                    : availableSubjects.length === 0 
                    ? 'No subjects available' 
                    : 'Select Subject'}
                </option>
                {availableSubjects.map((subject, index) => (
                  <option key={index} value={subject.name}>
                    {subject.name || 'Unknown Subject'} ({subject.mode || 'N/A'} - ${subject.rate}/hr)
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="mode">Mode *</label>
              <select
                id="mode"
                value={formData.mode}
                onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value }))}
                required
                disabled={!!formData.subject}
              >
                <option value="Online">Online</option>
                <option value="Physical">Physical</option>
              </select>
              {formData.subject && (
                <small className="field-hint">Mode is set by the subject enrollment</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date & Time *</label>
              <input
                type="datetime-local"
                id="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="duration">Duration (minutes) *</label>
              <input
                type="number"
                id="duration"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                min="15"
                step="15"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="rate">Tutee Rate ($) *</label>
              <input
                type="number"
                id="rate"
                value={formData.rate}
                onChange={(e) => setFormData(prev => ({ ...prev, rate: e.target.value }))}
                min="0"
                step="0.01"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="tutorRate">Tutor Rate ($)</label>
              <input
                type="number"
                id="tutorRate"
                value={formData.tutorRate}
                onChange={(e) => setFormData(prev => ({ ...prev, tutorRate: e.target.value }))}
                min="0"
                step="0.01"
                placeholder="Auto-calculated if empty"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="summary">Lesson Summary</label>
            <textarea
              id="summary"
              value={formData.summary}
              onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
              rows="3"
              placeholder="Optional lesson notes..."
            />
          </div>

          <div className="form-note">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 4v4M8 10h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>
              <strong>Admin Override:</strong> This will create a lesson regardless of endorsement or payment status. 
              You will be warned if the tutor invoice is already paid or tutee invoice is finalized.
            </span>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Lesson'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddLessonModal;
